import * as React from "react";
import { Toggle as TogglePrimitive } from "radix-ui";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";

import { cn } from "@papier/ui/lib/utils";

const segmentedGroupStyles =
  "segmented-group overflow-hidden rounded-lg border border-border/60 bg-transparent";

const toggleVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-control text-sm font-medium whitespace-nowrap transition-colors duration-150 outline-none hover:bg-muted/50 hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground motion-reduce:transition-none dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground",
        contrast:
          "border border-input data-[state=on]:bg-card-foreground data-[state=on]:text-accent",
        segmented:
          "relative min-w-0 gap-1 rounded-none text-xs text-muted-foreground not-first:before:absolute not-first:before:inset-y-2.5 not-first:before:left-0 not-first:before:w-px not-first:before:bg-border/70 not-first:before:content-[''] first:rounded-l-lg last:rounded-r-lg hover:bg-muted/40 focus-visible:ring-inset data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary single-segment:bg-transparent single-segment:bg-linear-to-r single-segment:from-primary/10 single-segment:to-transparent single-segment:not-first-of-type:not-last-of-type:from-transparent single-segment:not-first-of-type:not-last-of-type:via-primary/10 single-segment:last-of-type:bg-linear-to-l",
      },
      size: {
        default: "h-9 min-w-9 px-2",
        sm: "h-8 min-w-8 px-1.5",
        lg: "h-11 min-w-11 px-3",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
    compoundVariants: [
      { variant: "segmented", class: "px-1" },
      { variant: "segmented", size: "lg", class: "text-[13px] not-first:before:inset-y-3" },
    ],
  },
);

function Toggle({
  className,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof TogglePrimitive.Root> & VariantProps<typeof toggleVariants>) {
  return (
    <TogglePrimitive.Root
      data-slot="toggle"
      className={cn(toggleVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Toggle, toggleVariants, segmentedGroupStyles };
